import React, { useState, useEffect, useMemo } from "react";
import { ClipLoader } from "react-spinners";
import { Card, CardBody } from "reactstrap";
import "./styles.css";
import api from "../../../../../services/api";
import {
  formatCurrency,
  formatCurrencyInteiro,
} from "../../../../../utils/formatCurrency";
import { formatDate } from "../../../../../utils/formatDate";
import { useCompanies } from "../../../../../hooks/companies";
import FaturaDetalheModal, { rotuloSituacao } from "../FaturaDetalheModal";
import moment from "moment";

// Faixas de prazo: a mesma chave serve de filtro (legenda) e de cor do card.
const PRAZOS = [
  { key: "card-vencido", label: "Vencidas" },
  { key: "card-vai-vencer-7", label: "Até 7 dias" },
  { key: "card-vai-vencer-15", label: "Até 15 dias" },
  { key: "card-vai-vencer-30", label: "Até 30 dias" },
  { key: "card-futuro", label: "Mais de 30 dias" },
  { key: "card-padrao", label: "Integradas / pagas" },
];

const statuses = {
  "Falta CTe": "FaltaCTe",
  // PO1-3217: "Falta NF-e" = fatura esperando um CT-e que está parado por NF não
  // encontrada (antes "Falta Viagem", sempre vazia). "Com pendência" = situações
  // Em Validação, Em Análise e Em Tratativa (antes "Com Divergência").
  "Falta NF-e": "FaltaNFe",
  "A Validar": "FaturaIntegrada",
  "Com pendência": "FaturaComDivergencia",
  "Fatura Liberada": "FaturaLiberada",
  "Integrada ERP": "FaturaPaga",
  // PO1-3653: a 11 (Paga, pagamento confirmado) ganha coluna própria. O backend
  // devolve as finalizadas como FaturaPaga; a separação é pela situação (ver
  // normalizarStatus).
  Paga: "FaturaPagaConfirmada",
};

const SITUACAO_PAGA = 11;

// Status que já encerraram o fluxo: sem urgência de prazo no card.
const STATUS_ENCERRADOS = ["FaturaPaga", "FaturaPagaConfirmada"];

// Situação 11 vem do backend com StatusFatura FaturaPaga (mesmo lote da 8);
// troca para o status da coluna "Paga".
const normalizarStatus = (fatura) =>
  fatura && Number(fatura.Situacao) === SITUACAO_PAGA
    ? { ...fatura, StatusFatura: "FaturaPagaConfirmada" }
    : fatura;

const MAX_VISIBLE = 10;

const NOME_DA_COLUNA = Object.fromEntries(
  Object.entries(statuses).map(([nome, status]) => [status, nome])
);

// Cor do selo de prazo no modal (fundo escuro fixo, fora do tema do kanban).
const COR_DO_PRAZO = {
  "card-vencido": "#ff5c7a",
  "card-vai-vencer-7": "#ff9f43",
  "card-vai-vencer-15": "#f7d154",
  "card-vai-vencer-30": "#4fa3ff",
  "card-futuro": "#2dce89",
};

const getDiasParaVencimento = (dataVctoFatura) =>
  moment(dataVctoFatura).diff(moment(), "days");

const getCardColor = (dataVctoFatura, statusFatura) => {
  // Fatura já integrada no ERP ou paga não tem mais urgência de prazo.
  if (STATUS_ENCERRADOS.includes(statusFatura)) {
    return "card-padrao";
  }

  const diasParaVencimento = getDiasParaVencimento(dataVctoFatura);

  if (diasParaVencimento < 0) {
    return "card-vencido";
  } else if (diasParaVencimento <= 7) {
    return "card-vai-vencer-7";
  } else if (diasParaVencimento <= 15) {
    return "card-vai-vencer-15";
  } else if (diasParaVencimento <= 30) {
    return "card-vai-vencer-30";
  } else if (diasParaVencimento > 30) {
    return "card-futuro";
  } else {
    return "card-padrao";
  }
};

const getPrazoLabel = (dataVctoFatura, statusFatura) => {
  if (statusFatura === "FaturaPagaConfirmada") return "Paga";
  if (statusFatura === "FaturaPaga") return "Integrada";

  const dias = getDiasParaVencimento(dataVctoFatura);
  if (Number.isNaN(dias)) return "Sem vencimento";
  if (dias < 0) {
    const atraso = Math.abs(dias);
    return `Vencida há ${atraso} ${atraso === 1 ? "dia" : "dias"}`;
  }
  if (dias === 0) return "Vence hoje";
  return `Vence em ${dias} ${dias === 1 ? "dia" : "dias"}`;
};

const getCardId = (fatura) => {
  if (fatura && fatura.id !== undefined && fatura.id !== null) {
    return String(fatura.id);
  }
  if (fatura && fatura.NumeroFat) {
    return `fat-${fatura.NumeroFat}-${
      fatura.StatusFatura || "status-desconhecido"
    }`;
  }
  return `fallback-${
    fatura?.StatusFatura || "status-desconhecido"
  }-${fatura?.Transportadora || "transportadora-desconhecida"}-${
    fatura?.DataVctoFatura || "sem-data"
  }`;
};

// Card do kanban -> props do modal compartilhado. "Falta CT-e" e "Falta NF-e"
// ainda estão na Work_Fatura: sem situação nem Id (o modal não mostra o botão
// da Auditoria) e a situação exibida é a própria coluna.
const faturaDoCard = (fatura) => {
  if (!fatura) return null;
  const cor = getCardColor(fatura.DataVctoFatura, fatura.StatusFatura);
  return {
    numFatura: fatura.NumeroFat,
    valor: Number(fatura.ValorFat) || 0,
    transportadora: fatura.Transportadora,
    situacao: rotuloSituacao(
      fatura.Situacao,
      NOME_DA_COLUNA[fatura.StatusFatura]
    ),
    vencimento: fatura.DataVctoFatura,
    emissao: fatura.DataEmissao,
    qtdCtes: fatura.QtdCtes,
    idFatura: fatura.IdFatura,
    prazo: COR_DO_PRAZO[cor]
      ? {
          label: getPrazoLabel(fatura.DataVctoFatura, fatura.StatusFatura),
          cor: COR_DO_PRAZO[cor],
        }
      : null,
  };
};

const somaValores = (faturas) =>
  faturas.reduce((total, f) => total + (Number(f.ValorFat) || 0), 0);

const KanbanCard = ({ fatura, onSelect }) => {
  const cor = getCardColor(fatura.DataVctoFatura, fatura.StatusFatura);

  return (
    <div
      className={`kanban-item ${cor}`}
      onClick={() => onSelect(fatura)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelect(fatura);
        }
      }}
      title="Ver detalhes da fatura"
    >
      <div className="kanban-item-top">
        <span className="kanban-item-transportadora">
          {fatura.Transportadora}
        </span>
      </div>
      <div className="kanban-item-numero">Fatura {fatura.NumeroFat}</div>
      {/* ValorFat vem como string ("261.2"); o currency.js com decimal "," leria
          o ponto como milhar (R$ 2.612,00). Converter antes de formatar. */}
      <div className="kanban-item-valor">
        {formatCurrency(Number(fatura.ValorFat) || 0)}
      </div>
      <div className="kanban-item-footer">
        <span className="kanban-item-vencimento">
          <i className="tim-icons icon-calendar-60" />
          {formatDate(fatura.DataVctoFatura)}
        </span>
        <span className="kanban-item-prazo">
          {getPrazoLabel(fatura.DataVctoFatura, fatura.StatusFatura)}
        </span>
      </div>
    </div>
  );
};

const KanbanColumn = ({
  columnName,
  faturas,
  onSelect,
  visibleCount,
  setVisibleCount,
  maxVisible = MAX_VISIBLE,
}) => {
  // No xs (≤575px), começa colapsado pra não criar scroll infinito empilhado.
  // Em sm+, sempre aberto.
  const isMobile =
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(max-width: 575.98px)").matches
      : false;
  const [collapsed, setCollapsed] = useState(isMobile);

  // visibleCount is controlled by parent to persist per-column state
  const localVisible = Math.min(visibleCount ?? maxVisible, faturas.length);
  const visible = useMemo(
    () => faturas.slice(0, localVisible),
    [faturas, localVisible]
  );
  const remaining = Math.max(0, faturas.length - localVisible);
  const total = useMemo(() => somaValores(faturas), [faturas]);

  return (
    <section
      className={`kanban-column ${faturas.length === 0 ? "is-empty" : ""}`}
    >
      <header
        className="kanban-column-header"
        onClick={() => setCollapsed((c) => !c)}
      >
        <div className="kanban-column-heading">
          <span className="kanban-column-name">{columnName}</span>
          <span className="kanban-column-count">{faturas.length}</span>
          <span className="kanban-column-toggle" aria-hidden>
            {collapsed ? "▾" : "▴"}
          </span>
        </div>
        <div className="kanban-column-total">
          {faturas.length > 0 ? formatCurrencyInteiro(total) : "—"}
        </div>
      </header>
      {collapsed ? null : (
        <div className="kanban-column-body">
          {faturas.length === 0 && (
            <div className="kanban-column-empty">Nenhuma fatura</div>
          )}
          {visible.map((fatura) => {
            const cardId = getCardId(fatura);
            return (
              <KanbanCard
                key={cardId}
                fatura={fatura}
                onSelect={onSelect}
              />
            );
          })}
          {(remaining > 0 || localVisible > maxVisible) && (
            <div className="kanban-column-more">
              {remaining > 0 && (
                <button
                  type="button"
                  onClick={() =>
                    setVisibleCount(
                      Math.min(
                        faturas.length,
                        (visibleCount ?? maxVisible) + maxVisible
                      )
                    )
                  }
                >
                  Mostrar mais ({remaining})
                </button>
              )}
              {localVisible > maxVisible && (
                <button
                  type="button"
                  className="is-secondary"
                  onClick={() => setVisibleCount(maxVisible)}
                >
                  Mostrar menos
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

const KanbanStatusFatura = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [faturaSelecionada, setFaturaSelecionada] = useState(null);
  const [data, setData] = useState([]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [visibleCounts, setVisibleCounts] = useState({});

  // helper to set visible count for a column
  const setVisibleCountFor = (columnName, count) =>
    setVisibleCounts((p) => ({ ...p, [columnName]: count }));

  // PO1-3217: respeita a transportadora selecionada.
  const { transportadoraId, arrayParams } = useCompanies();

  useEffect(() => {
    async function loadFaturas() {
      try {
        setLoading(true);
        const params = {};
        if (arrayParams.transportadoras) {
          params.transportadoras = arrayParams.transportadoras;
        }
        const response = await api.get("/dashboard/auditoria/statusfatura", {
          params,
        });
        if (response.status === 200) {
          setData((response.data || []).map(normalizarStatus));
        }
      } catch (error) {
        console.error("Failed to fetch faturas", error);
      } finally {
        setLoading(false);
      }
    }
    loadFaturas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transportadoraId]);

  const handleFilterClick = (filter) => {
    setActiveFilter(filter === activeFilter ? null : filter); // Alterna entre aplicar e remover o filtro
  };

  const faturasValidas = useMemo(
    () => (data || []).filter((fatura) => fatura.NumeroFat !== null),
    [data]
  );

  // Contagem por faixa de prazo considera a busca, mas não o filtro de prazo,
  // pra legenda mostrar quanto cada filtro vai trazer.
  const faturasBuscadas = useMemo(() => {
    if (!searchTerm) return faturasValidas;
    return faturasValidas.filter((fatura) =>
      fatura.NumeroFat.toString().includes(searchTerm.trim())
    );
  }, [faturasValidas, searchTerm]);

  const contagemPorPrazo = useMemo(() => {
    const contagem = {};
    faturasBuscadas.forEach((fatura) => {
      const cor = getCardColor(fatura.DataVctoFatura, fatura.StatusFatura);
      contagem[cor] = (contagem[cor] || 0) + 1;
    });
    return contagem;
  }, [faturasBuscadas]);

  const filteredData = useMemo(() => {
    if (!activeFilter) return faturasBuscadas;
    return faturasBuscadas.filter(
      (fatura) =>
        getCardColor(fatura.DataVctoFatura, fatura.StatusFatura) ===
        activeFilter
    );
  }, [faturasBuscadas, activeFilter]);

  return (
    <div className="content">
      <Card className="kanban-card">
        <CardBody className="kanban-card-body">
          <div className="kanban-toolbar">
            <div className="kanban-toolbar-title">
              <h3>Status das Faturas</h3>
              <p>Filtre pelo prazo de vencimento ou busque pelo número</p>
            </div>
            <div className="kanban-search">
              <i className="tim-icons icon-zoom-split" />
              <input
                type="text"
                placeholder="Número da fatura"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  aria-label="Limpar busca"
                  onClick={() => setSearchTerm("")}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="kanban-legend">
            {PRAZOS.map(({ key, label }) => (
              <button
                type="button"
                key={key}
                className={`legend-item ${key} ${
                  activeFilter === key ? "active" : ""
                }`}
                onClick={() => handleFilterClick(key)}
                aria-pressed={activeFilter === key}
              >
                <span className="legend-dot" />
                {label}
                <span className="legend-count">
                  {contagemPorPrazo[key] || 0}
                </span>
              </button>
            ))}
            {activeFilter && (
              <button
                type="button"
                className="legend-clear"
                onClick={() => setActiveFilter(null)}
              >
                Limpar filtro
              </button>
            )}
          </div>

          {loading ? (
            <div className="kanban-loading">
              <ClipLoader
                sizeUnit="px"
                size={48}
                color="var(--theme-accent, #1d8cf8)"
                loading={loading}
              />
              <span>Carregando faturas…</span>
            </div>
          ) : (
            <div className="kanban-board">
              {Object.entries(statuses).map(([columnName, statusFilter]) => {
                const faturasForColumn = filteredData.filter(
                  (fatura) => fatura.StatusFatura === statusFilter
                );
                const visibleForColumn =
                  visibleCounts[columnName] ??
                  Math.min(MAX_VISIBLE, faturasForColumn.length);

                return (
                  <KanbanColumn
                    key={columnName}
                    columnName={columnName}
                    faturas={faturasForColumn}
                    onSelect={setFaturaSelecionada}
                    visibleCount={visibleForColumn}
                    setVisibleCount={(n) => setVisibleCountFor(columnName, n)}
                    maxVisible={MAX_VISIBLE}
                  />
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
      <FaturaDetalheModal
        fatura={faturaDoCard(faturaSelecionada)}
        onClose={() => setFaturaSelecionada(null)}
      />
    </div>
  );
};

export default KanbanStatusFatura;
